import { CustomPIXIComponent } from 'react-pixi-fiber'
import { Container } from 'pixi.js'
import { Tween, Easing } from '@tweenjs/tween.js'
import { startLoop, stopLoop } from 'macguffin/Utils/Loop'

import { Symbol, CometSymbol, ScatterSymbol } from './symbols'

import ArrayHelper from 'utils/ArrayHelper'
import {
  createTween,
  chainTweens
} from 'utils/TweenHelper'

import GameMode from 'game/modes'

const { min, max } = Math

const { FREESPIN } = GameMode

const WIN_ALPHA = 0.75
const WIN_FILTER = new PIXI.filters.ColorMatrixFilter()
WIN_FILTER.greyscale(0.3)

const WR = 'WR'
const WR_FS = 'WR_FS'
const RAND = 'RAND'
const SC = 'SC'

const NORMAL_SYMBOLS = [
  'H1',
  'H2',
  'H3',
  'M4',
  'M5',
  'M6',
  'L7',
  'L8',
  'L9',
  'L10',
  'WR'
]

const SYMBOL_SCALE = 0.9

const BLUR_SPEED = 0.1
const SPIN_SPEED = 0.3
const ANTICIPATION_SPEED = 0.8
const SLOW_SPEED = 0.15

const BUMP_HEIGHT = -0.4
const BUMP_UP_DURATION = 250
const BUMP_UP_EASING = Easing.Quadratic.Out
const BUMP_DOWN_DURATION = 250
const BUMP_DOWN_EASING = Easing.Quadratic.In
const PREPARE_BOUNCE_DURATION = 1000
const BOUNCE_HEIGHT = 0.6
const BOUNCE_UP_DURATION = 500
const BOUNCE_UP_EASING = Easing.Elastic.Out

const ANTICIPATION_DURATION = 250
const SLOW_DURATION = 500

const wrapIndex = (index, array) => {
  const length = array.length
  return (length + index) % length
}

const createSymbol = (symbolData, currentGameMode) => {
  if (NORMAL_SYMBOLS.indexOf(symbolData) >= 0) {
    const symbolKey = symbolData !== 'WR' ? symbolData : 'WR_FS'
    return new Symbol(symbolKey)
  }

  if (symbolData === RAND || symbolData.symbol) {
    if (symbolData.symbol !== 'H1') {
      return new CometSymbol(symbolData)
    } else {
      return new Symbol(symbolData.symbol)
    }
  }

  if (symbolData === 'SC') {
    return new ScatterSymbol(symbolData, currentGameMode === FREESPIN)
  }
}

class Strip {
  constructor(reel, replacements, symbols) {
    this.getStop = index => {
      return wrapIndex(index, reel)
    }

    this.getSymbol = index => {
      const wrappedIndex = wrapIndex(index, reel)
      const symbol = reel[wrappedIndex]

      const replacement =
        replacements &&
        replacements.find(element => {
          return element.y - 1 === index
        })

      return replacement || symbol
    }
  }
}

const hideContainers = containers => {
  containers.forEach(container => {
    container.visible = false
  })
}

const showContainers = containers => {
  containers.forEach(container => {
    container.visible = true
  })
}

class Reel extends Container {
  constructor(props) {
    super(props)
    const { index, stop, symbols, reel, replacements, currentGameMode } = props
    this.index = index
    this.currentSpeed = 0

    this.updateStrip(reel, replacements)

    const stops = []
    for (let i = 0; i < 5; ++i) {
      const container = new Container()
      stops[i] = symbols[i - 1] || wrapIndex(stop + i - 2, reel)

      const symbolKey = symbols[i - 1] || this.strip.getSymbol(stops[i])
      const symbol = createSymbol(symbolKey, currentGameMode)
      container.addChild(symbol)
      this.addChild(container)
    }
    this.stops = stops

    this.update(props)
  }

  update = props => {
    const {
      width,
      height,
      reel,
      symbols,
      currentGameMode,
      setCometFlypast,
      setReelStopping
    } = props
    const { index, children, updateSymbol } = this

    const symbolHeight = height / 3
    const symbolWidth = symbolHeight

    this.x = width * index + width * 0.5
    this.symbolWidth = symbolWidth
    this.symbolHeight = symbolHeight

    children.forEach((child, index) => {
      child.y = symbolHeight * (index - 0.5)
      const symbol = child.children[0]
      updateSymbol(symbol)
    })

    this.reel = reel
    this.symbols = symbols
    this.currentGameMode = currentGameMode
    this.setCometFlypast = setCometFlypast
    this.setReelStopping = setReelStopping
  }

  updateStrip = (reel, replacements) => {
    this.strip = new Strip(reel, replacements)
  }

  updateSymbolStops = () => {
    const {
      stops,
      topStop,
      finalStop,
      children,
      strip,
      currentGameMode,
      updateSymbol,
      bounce
    } = this

    for (let i = 5; i >= 1; --i) {
      stops[i] = stops[i - 1]
    }
    stops[0] = this.topStop = strip.getStop(topStop - 1)

    for (let i = 0; i < 5; ++i) {
      const child = children[i]
      child.removeChildren()

      const symbolKey = !isNaN(stops[i]) ? strip.getSymbol(stops[i]) : stops[i]
      const symbol = createSymbol(symbolKey, currentGameMode)
      child.addChild(symbol)
      updateSymbol(symbol)
    }

    if (stops[0] === finalStop) {
      bounce()
    }
  }

  updateSymbol = symbol => {
    const { symbolWidth, symbolHeight, currentSpeed } = this

    if (currentSpeed >= BLUR_SPEED) {
      symbol.blur()
    } else {
      symbol.unblur()
    }

    symbol.update({ width: symbolWidth, height: symbolHeight })
  }

  animate = delta => {
    const {
      children,
      currentSpeed,
      symbolHeight,
      updateSymbol,
      updateSymbolStops,
      setCometFlypast
    } = this

    for (let i = 0; i < 5; ++i) {
      const child = children[i]
      const symbol = child.children[0]
      updateSymbol(symbol)
      symbol.y += symbolHeight * currentSpeed * (delta / 20)

      if (symbol.y >= symbolHeight) {
        if (i === 4) {
          if (symbol instanceof CometSymbol) {
            setCometFlypast()
          }

          updateSymbolStops()
        }
        symbol.y = 0
      }
    }
  }

  startSpin = props => {
    const { children, strip, symbolHeight } = this
    const { stop } = props

    showContainers(children)

    this.topStop = strip.getStop(stop - 2)
    this.finalStop = null
    this.skip = false
    this.stopTweens = undefined

    startLoop(this.animate)

    const bumpUp = new Tween(this)
      .to({ y: symbolHeight * BUMP_HEIGHT }, BUMP_UP_DURATION)
      .easing(BUMP_UP_EASING)

    const bumpDown = new Tween(this)
      .to({ y: 0, currentSpeed: SPIN_SPEED }, BUMP_DOWN_DURATION)
      .easing(BUMP_DOWN_EASING)

    const prepareBounce = new Tween(this).to(
      { y: symbolHeight * BOUNCE_HEIGHT },
      PREPARE_BOUNCE_DURATION
    )

    this.prepareBounce = prepareBounce
    chainTweens([bumpUp, bumpDown, prepareBounce]).start()
  }

  skipSpin = props => {
    const {
      index,
      skip,
      stopTweens,
      strip,
      symbolHeight,
      topStop,
      finalStop
    } = this
    const { reelStoppedIndex, anticipationSymbols, stop, onComplete } = props

    if (!skip && index > reelStoppedIndex && topStop !== finalStop) {
      stopTweens &&
        stopTweens.forEach(tween => {
          tween.stop()
        })
      this.currentSpeed = SPIN_SPEED

      const finalStop = (this.finalStop = strip.getStop(stop - 2))
      this.topStop = strip.getStop(finalStop + 6 + index)

      this.onComplete = onComplete
    }

    this.skip = true
  }

  stopSpin = props => {
    const { setStopSymbols } = this
    const { spinDuration, defaultSpinDuration, onComplete } = props
    this.onComplete = onComplete

    const anticipationDuration = spinDuration - defaultSpinDuration
    const anticipationSpeed =
      anticipationDuration > 0 ? ANTICIPATION_SPEED : SPIN_SPEED
    const remainingDuration = anticipationDuration - ANTICIPATION_DURATION
    const setFinalSymbols = () => {
      setStopSymbols(props)
    }

    const preDelayTween = new Tween().to({}, defaultSpinDuration)

    const anticipateTween = new Tween(this).to(
      { currentSpeed: anticipationSpeed },
      ANTICIPATION_DURATION
    )

    const postDelayTween = new Tween(this)
      .to({ currentSpeed: SLOW_SPEED }, max(0, remainingDuration))
      .onComplete(setFinalSymbols)

    if (!anticipationDuration) {
      this.stopTweens = [preDelayTween]
      preDelayTween.onComplete(setFinalSymbols).start()
    } else {
      const tweens = [preDelayTween, anticipateTween, postDelayTween]
      this.stopTweens = tweens
      chainTweens(tweens).start()
    }
  }

  setStopSymbols = props => {
    const { strip, symbolHeight } = this
    const { stop, anticipationSymbols } = props
    const finalStop = (this.finalStop = strip.getStop(stop - 2))
    this.topStop = strip.getStop(finalStop + anticipationSymbols)
  }

  turnWild = containers => {
    const { currentGameMode, symbolWidth, symbolHeight, updateSymbol } = this

    const symbolSize = { width: symbolWidth, height: symbolHeight }

    if (currentGameMode !== FREESPIN) {
      containers.forEach(child => {
        const symbol = child.children[0]
        symbol.turnWild()
        symbol.update(symbolSize)
      })
    }
  }

  bounce = () => {
    const {
      children,
      symbols,
      index,
      prepareBounce,
      setReelStopping,
      turnWild,
      onComplete,
      currentGameMode
    } = this
    prepareBounce && prepareBounce.stop()
    this.currentSpeed = 0

    const wildChildren = children.filter((child, index) => {
      const symbol = child.children[0]
      const reelSymbol = symbols[index - 1]
      return symbol.key === WR || reelSymbol === WR
    })

    const scatterChildren = children.filter(child => {
      const symbol = child.children[0]
      return symbol.key === SC
    })

    const bounce = new Tween(this)
      .to({ y: 0 }, BOUNCE_UP_DURATION)
      .easing(BOUNCE_UP_EASING)
      .onStart(() => {
        setReelStopping(index)
      })
      .onComplete(() => {
        if (currentGameMode !== GameMode.REVERT) {
          turnWild(wildChildren)
          hideContainers(scatterChildren)
        }
        stopLoop(this.animate)
        onComplete && onComplete(index)
        this.onComplete = undefined
      })

    bounce.start()
  }

  hideWinningSymbols = props => {
    const { index, children } = this
    const { currentWinData, currentWinIteration } = props

    const winningSymbols = !isNaN(currentWinIteration)
      ? currentWinData[currentWinIteration].winningSymbols
      : currentWinData.reduce(
          (combined, current) => combined.concat(current.winningSymbols),
          []
        )

    children.forEach((child, childIndex) => {
      const isWinning = winningSymbols.find(symbol => {
        const { x, y } = symbol
        return x - 1 === index && y === childIndex
      })
      const symbol = child.children[0]
      child.visible = !isWinning || symbol.key === WR || symbol.key === WR_FS

      if (symbol.key === SC) {
        child.visible = false
      }
    })
  }

  unhideSymbols = () => {
    const { children } = this
    showContainers(children)
  }
}

//CUSTOM REACT-PIXI CLASS
export default CustomPIXIComponent({
  customDisplayObject(props) {
    return new Reel(props)
  },

  customApplyProps(displayObject, oldProps, newProps) {
    displayObject.update(newProps)

    displayObject.alpha = newProps.currentWinData ? WIN_ALPHA : 1
    displayObject.filters = newProps.currentWinData ? [WIN_FILTER] : []

    if (
      newProps.currentWinData !== oldProps.currentWinData ||
      newProps.currentWinIteration !== oldProps.currentWinIteration
    ) {
      if (newProps.currentWinData && newProps.currentWinData.length) {
        displayObject.hideWinningSymbols(newProps)
      } else {
        displayObject.unhideSymbols()
      }
    }

    if (newProps.isSpinning !== oldProps.isSpinning && newProps.isSpinning) {
      displayObject.startSpin(newProps)
    }

    if (newProps.isStopping !== oldProps.isStopping && newProps.isStopping) {
      displayObject.updateStrip(newProps.reel, newProps.replacements)
      displayObject.stopSpin(newProps)
    }

    if (newProps.isSkipping !== oldProps.isSkipping && newProps.isSkipping) {
      if (newProps.isSpinning && newProps.isStopping) {
        displayObject.skipSpin(newProps)
      }
    }
  }
}, 'Reel')
